import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ShieldCheck, Flame, Play, CheckCircle2, Truck, RefreshCw, CreditCard,
  Headphones, Quote, MapPin, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Counter from '../components/Counter';
import Marquee from '../components/Marquee';
import VideoModal from '../components/VideoModal';
import {
  COMPANY, HERO_IMAGES, HERO_VIDEO_ID, STATS, PRODUCT_RANGE, SOLUTIONS, SUCCESS_STORIES,
  WHY_US, APPROVALS, CLIENTS, VIDEOS, SITUATION, FAQS, FOUNDER,
} from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import BentoGrid from '../components/BentoGrid';
import api, { mediaUrl, asArray } from '../lib/api';

const WHY_ICONS = [Truck, RefreshCw, CreditCard, Headphones];

function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <div className={`max-w-2xl mb-14 ${center ? 'mx-auto text-center' : ''}`}>
      <div className={`section-eyebrow fire-text ${center ? 'justify-center' : ''}`}><Sparkles className="w-4 h-4 text-primary" /> {eyebrow}</div>
      <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white tracking-tight">{title}</h2>
      {sub && <p className="mt-4 text-lg text-gray-400">{sub}</p>}
    </div>
  );
}

export default function Home() {
  const [video, setVideo] = useState(null);
  const [vidReady, setVidReady] = useState(false);
  const [settings, setSettings] = useState(null);
  const [solutions, setSolutions] = useState(SOLUTIONS);
  const [stories, setStories] = useState(SUCCESS_STORIES);
  const [featured, setFeatured] = useState([]);
  const [clients, setClients] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    api.get('/settings').then((r) => { if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) setSettings(r.data); }).catch(() => {});
    api.get('/solutions').then((r) => { const a = asArray(r.data); if (a.length) setSolutions(a); }).catch(() => {});
    api.get('/stories').then((r) => { const a = asArray(r.data); if (a.length) setStories(a); }).catch(() => {});
    api.get('/products/featured').then((r) => setFeatured(asArray(r.data))).catch(() => {});
    api.get('/clients').then((r) => setClients(asArray(r.data))).catch(() => {});
    api.get('/certifications').then((r) => setCerts(asArray(r.data))).catch(() => {});
  }, []);

  const heroPoster = settings?.heroPosterUrl || HERO_IMAGES[0];
  const heroVideo = settings?.heroVideoUrl;

  // Guaranteed arrays at the point of use — never call .slice()/.map() on a
  // non-array (e.g. if a misconfigured backend returns an HTML string/object).
  const featuredList = Array.isArray(featured) ? featured : [];
  const solutionList = Array.isArray(solutions) ? solutions : SOLUTIONS;
  const storyList = Array.isArray(stories) ? stories : SUCCESS_STORIES;
  const certList = Array.isArray(certs) && certs.length ? certs : APPROVALS.map((a) => ({ name: a }));
  const clientList = Array.isArray(clients) && clients.length ? clients : CLIENTS.map((c) => ({ name: c }));

  return (
    <div className="bg-[#070a12]">
      <VideoModal videoId={video} onClose={() => setVideo(null)} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden noise">
        <div className="absolute inset-0">
          <img src={heroPoster} alt="Fire safety" className="w-full h-full object-cover animate-kenburns" />
          <video
            key={heroVideo || 'novideo'}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: vidReady ? 1 : 0 }}
            autoPlay muted loop playsInline preload="auto"
            poster={heroPoster}
            onCanPlay={() => setVidReady(true)}
          >
            {heroVideo && <source src={heroVideo} type="video/mp4" />}
            <source src="https://videos.pexels.com/video-files/35052234/14848234_640_360_60fps.mp4" type="video/mp4" />
            <source src="https://assets.mixkit.co/videos/1082/1082-720.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 hero-vignette" />
        <div className="orb w-[500px] h-[500px] bg-primary/25 top-10 -left-40 animate-glow" />
        <div className="orb w-[420px] h-[420px] bg-accent/20 bottom-0 right-0 animate-glow" style={{ animationDelay: '2s' }} />

        <div className="relative max-w-7xl mx-auto px-6 w-full pt-28 pb-16">
          <motion.div className="max-w-3xl" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <div className="section-eyebrow glass rounded-full px-4 py-2 text-white/90"><Flame className="w-4 h-4 text-primary" /> {COMPANY.tagline}</div>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.02] mt-6 text-white text-balance">
              Your Partner in <span className="fire-text">Fire Prevention</span> & Protection
            </h1>
            <p className="text-lg text-gray-300 mt-6 max-w-xl">{COMPANY.heroSubtitle}</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Button asChild size="lg" className="gap-2 h-13 px-7 text-base bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-xl shadow-primary/30" style={{ height: 52 }}>
                <Link to="/products">Explore Products <ArrowRight className="w-4 h-4" /></Link>
              </Button>
              <button onClick={() => setVideo(HERO_VIDEO_ID)} className="group flex items-center gap-3 text-white font-heading font-semibold">
                <span className="relative grid place-items-center w-13 h-13 rounded-full glass" style={{ width: 52, height: 52 }}>
                  <Play className="w-5 h-5 fill-white" />
                  <span className="absolute inset-0 rounded-full border border-primary/60 animate-ping opacity-60" />
                </span>
                Watch Showreel
              </button>
            </div>
          </motion.div>

          {/* Floating stats */}
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}>
            {STATS.map((s) => (
              <div key={s.label} className="glass rounded-2xl p-5">
                <div className="font-display font-extrabold text-4xl fire-text"><Counter to={s.value} suffix={s.suffix} /></div>
                <div className="text-gray-400 text-sm font-heading font-semibold mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative py-28 overflow-hidden">
        <div className="orb w-[400px] h-[400px] bg-primary/10 top-20 -right-40" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="img-zoom rounded-3xl overflow-hidden grad-border">
              <img src={HERO_IMAGES[2]} alt="About Closefire" className="w-full h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-4 md:right-8 glass rounded-2xl p-6 w-52">
              <div className="font-display font-extrabold text-5xl fire-text"><Counter to={8} suffix="+" /></div>
              <div className="text-gray-300 font-heading font-semibold text-sm">Years protecting life & property</div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <SectionHead eyebrow="Who We Are" title="We Are Closefire" />
            <p className="text-gray-400 text-lg leading-relaxed -mt-8">{COMPANY.name} is a well-established name in the fire safety sector, recognised for its effectiveness and modern technology. With 8+ years of experience, we deliver comprehensive, certified fire safety solutions.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {['ISI-certified manufacturing', 'Rapid pan-India installation', 'Addressable detection systems', 'Lifetime AMC support'].map((m) => (
                <div key={m} className="flex gap-3"><ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" /><span className="text-sm text-gray-200 font-medium">{m}</span></div>
              ))}
            </div>
            <Button asChild className="mt-9 gap-2 bg-gradient-to-r from-primary to-accent"><Link to="/about">More About Us <ArrowRight className="w-4 h-4" /></Link></Button>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER'S MESSAGE */}
      <section className="relative py-28 overflow-hidden bg-[#05070d]" data-testid="founder-section">
        <div className="orb w-[420px] h-[420px] bg-accent/10 top-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[minmax(0,340px)_1fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <div className="relative mx-auto w-full max-w-[340px]">
              <div className="rounded-3xl overflow-hidden grad-border bg-black" style={{ aspectRatio: '9 / 16' }}>
                <iframe
                  src={FOUNDER.videoEmbed}
                  title="Founder's message"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <a href={FOUNDER.reelUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary font-heading font-semibold hover:underline">Watch on Instagram <ArrowRight className="w-4 h-4" /></a>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="section-eyebrow fire-text"><Quote className="w-4 h-4 text-primary" /> Founder's Message</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white">{FOUNDER.name}</h2>
            <p className="text-primary font-heading font-semibold mt-2">{FOUNDER.title}</p>
            <div className="mt-6 space-y-4 text-gray-400 leading-relaxed max-w-3xl">
              {FOUNDER.message.map((para, i) => (
                <p key={i} className={i === FOUNDER.message.length - 1 ? 'text-white font-heading font-semibold text-lg' : ''}>{para}</p>
              ))}
            </div>
            <div className="mt-6 border-l-2 border-primary pl-4">
              <div className="font-display font-bold text-white text-lg">{FOUNDER.name}</div>
              <div className="text-sm text-gray-500">Founder &amp; Director, FCP Closefire India</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featuredList.length > 0 && (
        <section className="py-28" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal><SectionHead center eyebrow="Featured" title="Our Star Products" sub="Hand-picked equipment our fire-safety experts recommend most." /></Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredList.slice(0, 8).map((p, i) => (
                <Reveal key={p.id} delay={(i % 4) * 90}>
                  <Link to={`/product/${p.id}`} data-testid={`featured-card-${p.id}`} className="group block glass glass-hover rounded-3xl overflow-hidden h-full">
                    <div className="img-zoom h-52 relative">
                      <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent to-transparent" />
                      <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/30">Featured</span>
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-semibold fire-text uppercase tracking-wider">{p.category}</span>
                      <h3 className="font-heading font-bold text-xl text-white mt-1 group-hover:fire-text transition-all">{p.name}</h3>
                      <p className="text-sm text-gray-400 mt-2 leading-relaxed line-clamp-2">{p.desc}</p>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-primary font-semibold text-sm">View details <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT RANGE */}
      <section className="py-28 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionHead center eyebrow="Products Range" title="Certified Fire Safety Equipment" sub="A comprehensive range of fire protection products engineered and tested to national standards." /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCT_RANGE.map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <Link to="/products" className="group block glass glass-hover rounded-3xl overflow-hidden h-full">
                  <div className="img-zoom h-52 relative">
                    <img src={mediaUrl(settings?.productRangeImages?.[p.title]) || p.image} alt={p.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-white group-hover:fire-text transition-all">{p.title}</h3>
                    <p className="text-sm text-gray-400 mt-2 leading-relaxed">{p.desc}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-primary font-semibold text-sm">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO GRID (finseo-style) */}
      <BentoGrid />

      {/* VIDEO SHOWCASE */}
      <section className="relative py-28 overflow-hidden">
        <div className="orb w-[500px] h-[500px] bg-accent/10 -top-20 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Reveal><SectionHead center eyebrow="Fire Safety in Motion" title="See Our Expertise at Work" sub="Watch how modern fire protection systems detect, alert and suppress fire before it spreads." /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VIDEOS.map((v, i) => (
              <Reveal key={v.id} delay={i * 90}>
                <button onClick={() => setVideo(v.id)} className="group relative w-full rounded-3xl overflow-hidden aspect-[3/4] grad-border">
                  <img src={v.thumb} alt={v.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
                  <span className="absolute top-4 left-4 text-xs font-heading font-bold uppercase tracking-wider px-3 py-1 rounded-full glass text-white">{v.tag}</span>
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-16 h-16 rounded-full bg-primary/90 text-white shadow-xl shadow-primary/40 group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 fill-white translate-x-0.5" />
                  </span>
                  <div className="absolute bottom-0 p-5 text-left"><h3 className="font-heading font-bold text-white leading-snug">{v.title}</h3></div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-28 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionHead center eyebrow="Areas of Operation" title="End-to-End Fire Solutions" sub="From design to commissioning and annual maintenance \u2014 we cover every fire safety discipline." /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutionList.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 90}>
                <div className="group relative rounded-3xl overflow-hidden h-64 grad-border">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/40 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <span className="section-eyebrow fire-text text-xs">Closefire</span>
                    <h3 className="font-heading font-bold text-xl text-white mt-1">{s.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SITUATION TODAY */}
      <section className="relative py-28 overflow-hidden">
        <div className="orb w-[400px] h-[400px] bg-primary/10 bottom-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionHead eyebrow={SITUATION.eyebrow} title={SITUATION.title} />
            <p className="text-gray-400 text-lg leading-relaxed -mt-8">{SITUATION.body}</p>
            <div className="space-y-4 mt-8">
              {SITUATION.points.map((p) => (
                <div key={p} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" /><span className="text-gray-200">{p}</span></div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="img-zoom rounded-3xl overflow-hidden grad-border">
            <img src={HERO_IMAGES[1]} alt="Fire expertise" className="w-full h-[460px] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="py-28 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <div><div className="section-eyebrow fire-text"><Sparkles className="w-4 h-4 text-primary" /> Success Stories</div><h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white">Projects That Speak for Us</h2></div>
            <Button asChild variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10 gap-2"><Link to="/projects">All Projects <ArrowRight className="w-4 h-4" /></Link></Button>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            {storyList.map((s, i) => (
              <Reveal key={s.id} delay={i * 100}>
                <article className="glass glass-hover rounded-3xl overflow-hidden h-full flex flex-col">
                  <div className="img-zoom h-52"><img src={s.image} alt={s.title} className="w-full h-full object-cover" /></div>
                  <div className="p-6 flex flex-col flex-1">
                    <Quote className="w-7 h-7 text-primary/40" />
                    <h3 className="font-heading font-bold text-lg text-white mt-2 leading-snug">{s.title}</h3>
                    <p className="text-sm text-gray-400 mt-3 line-clamp-3 flex-1">{s.excerpt}</p>
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-4"><MapPin className="w-3.5 h-3.5" /> {s.location}</div>
                    <Link to="/projects" className="mt-3 inline-flex items-center gap-1.5 text-primary font-semibold text-sm">Read more <ArrowRight className="w-4 h-4" /></Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROVALS + CLIENTS marquees */}
      <section className="py-20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 mb-10"><p className="text-center section-eyebrow text-gray-500 justify-center">Approvals & Certifications</p></div>
        <Marquee items={certList} className="mb-14" renderItem={(c) => (
          c.logo
            ? <img src={mediaUrl(c.logo)} alt={c.name} data-testid="cert-marquee-logo" className="h-20 w-auto object-contain mx-8 opacity-80 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-110 hover:drop-shadow-[0_0_16px_rgba(232,51,42,0.5)]" />
            : <span data-testid="cert-marquee-item" className="font-display font-bold text-2xl text-white/90 whitespace-nowrap px-4">{c.name}</span>
        )} />
        <div className="max-w-7xl mx-auto px-6 mb-10"><p className="text-center section-eyebrow text-gray-500 justify-center">Trusted by Industry Leaders</p></div>
        <Marquee items={clientList} renderItem={(c) => (
          c.logo
            ? <img src={mediaUrl(c.logo)} alt={c.name} data-testid="client-marquee-logo" className="h-14 w-auto object-contain mx-8 opacity-70 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-110 hover:drop-shadow-[0_0_16px_rgba(232,51,42,0.5)]" />
            : <span data-testid="client-marquee-item" className="font-heading font-semibold text-lg text-gray-300 whitespace-nowrap px-4">{c.name}</span>
        )} />
      </section>

      {/* WHY US */}
      <section className="py-28 bg-[#05070d]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 90}>
                  <div className="p-7 rounded-3xl glass glass-hover h-full">
                    <span className="grid place-items-center w-13 h-13 rounded-2xl bg-gradient-to-br from-primary to-accent text-white mb-5" style={{ width: 52, height: 52 }}><Icon className="w-6 h-6" /></span>
                    <h3 className="font-heading font-bold text-lg text-white">{w.title}</h3>
                    <p className="text-sm text-gray-400 mt-2">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 bg-[#05070d]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal><SectionHead center eyebrow="FAQ" title="Frequently Asked Questions" sub="Everything you need to know about our fire safety products and services." /></Reveal>
          <Reveal delay={100}>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="glass rounded-2xl border-none px-6">
                  <AccordionTrigger className="text-left font-heading font-bold text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-400 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <img src={HERO_IMAGES[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#070a12]/85" />
        <div className="orb w-[400px] h-[400px] bg-primary/25 top-0 left-1/4 animate-glow" />
        <Reveal className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl text-white text-balance">Protect Your Premises <span className="fire-text">Today</span></h2>
          <p className="text-gray-300 text-lg mt-5 max-w-2xl mx-auto">To ensure the safety of your premises, contact our fire-safety experts for a free on-site assessment.</p>
          <Button asChild size="lg" className="mt-9 h-13 px-8 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-base gap-2 shadow-xl shadow-primary/30" style={{ height: 52 }}><Link to="/contact">Get a Free Consultation <ArrowRight className="w-4 h-4" /></Link></Button>
        </Reveal>
      </section>
    </div>
  );
}
